import { useState } from "react";
import useFetchDomains from "../hooks/useFetchDomains";
import useCreateDomain from "../hooks/useCreateDomain";
import useDeleteDomain from "../hooks/useDeleteDomain";
import "./AdminDomainListScreen.css";
import "./LoaderUI.css";
import { useNavigate } from "react-router-dom";

export default function AdminDomainListScreen() {

    const { domains, loadingDomainList } = useFetchDomains();
    const { createDomain, creatingDomain, creatingDomainError } = useCreateDomain();
    const { deleteDomain, deletingDomain, deletingDomainError } = useDeleteDomain();

    const [isAddDomainVisible, setIsAddDomainVisible] = useState(false);
    const [newDomainName, setNewDomainName] = useState("");
    const [newDomainOrder, setNewDomainOrder] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editedName, setEditedName] = useState("");
    const [editedOrder, setEditedOrder] = useState("");

    const navigate = useNavigate();

    const handleCreateDomain = async () => {
        if (!newDomainName.trim()) {
            alert("Please enter a domain name");
            return;
        }
        if (!newDomainOrder.trim()) {
            alert("Please enter an order ID");
            return;
        }

        const orderValue = parseInt(newDomainOrder, 10);
        if (isNaN(orderValue)) {
            alert("Order ID must be a number");
            return;
        }

        const success = await createDomain(newDomainName, null, orderValue);
        if (success) {
            setIsAddDomainVisible(false);
            setNewDomainName("");
            setNewDomainOrder("");
        }
    };

    // ✅ Start editing a domain
    const handleEditClick = (domain) => {
        setEditingId(domain.id);
        setEditedName(domain.name);
        setEditedOrder(domain.orderId);
    };

    // ✅ Save edited domain
    const handleSaveClick = async (domainId) => {
        if (!editedName.trim()) {
            alert("Domain name cannot be empty");
            return;
        }
        if (!editedOrder.toString().trim()) {
            alert("Order ID cannot be empty");
            return;
        }

        const orderValue = parseInt(editedOrder, 10);
        if (isNaN(orderValue)) {
            alert("Order ID must be a valid number");
            return;
        }

        const success = await createDomain(editedName, domainId, orderValue);
        if (success) {
            console.log("✅ Domain updated:", domainId);
            setEditingId(null);
            setEditedName("");
            setEditedOrder("");
        }
    };

    const handleDelete = async (domain) => {
        const confirmed = window.confirm(`Are you sure you want to delete "${domain.name}"?`);
        if (!confirmed) { 
            return; 
        }
        await deleteDomain(domain.id);
    };

    const handleDomainClick = (domainId) => { 
        navigate(`/admin/domain/${domainId}`)
    }

    return (
        <div className="domain-page">
            <div className="domain-container">
                <h1 className="domain-title fixed-title">IT Domains</h1>

                {/* Scrollable List Section */}
                <div className="domain-list-section">
                    {loadingDomainList ? (
                        <p className="info-text">Loading domains...</p>
                    ) : domains.length === 0 ? (
                        <p className="info-text italic">No domains found.</p>
                    ) : (
                        <ul className="domain-list">
                            {domains.map((d) => (
                                <li
                                    key={d.id}
                                    className="domain-item"
                                    onClick={() => { handleDomainClick(d.id)}}
                                >
                                    <div className="domain-details">
                                        {editingId === d.id ? (
                                            <>
                                                <input
                                                    type="text"
                                                    value={editedName}
                                                    className="edit-input"
                                                    onChange={(e) => setEditedName(e.target.value)}
                                                    autoFocus
                                                />
                                                <input
                                                    type="number"
                                                    value={editedOrder}
                                                    className="edit-input order-input"
                                                    onChange={(e) => setEditedOrder(e.target.value)}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <p className="domain-name">{d.name}</p>
                                                <p className="domain-desc">Order: {d.orderId}</p>
                                            </>
                                        )}
                                    </div>

                                    <div className="domain-actions">
                                        {editingId === d.id ? (
                                            <button
                                                className="save-btn"
                                                onClick={() => handleSaveClick(d.id)}
                                                disabled={creatingDomain}
                                            >
                                                {creatingDomain ? "Saving..." : "💾 Save"}
                                            </button>
                                        ) : (
                                            <button
                                                className="edit-btn"
                                                onClick={() => handleEditClick(d)}
                                            >
                                                ✏️ Edit
                                            </button>
                                        )}
                                        <button
                                            className="delete-btn" onClick={() => handleDelete(d) }>🗑️ Delete</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Floating Add Button */}
                <div className="fixed-bottom-btn">
                    <button className="add-btn"
                        onClick={() => setIsAddDomainVisible(true)}>
                        ➕ Add Domain
                    </button>
                </div>

                {/* Popup for Adding Domain */}
                {/* Add Domain Modal */}
                {isAddDomainVisible && (
                    <div
                        className="overlay"
                        onClick={() => setIsAddDomainVisible(false)}
                    >
                        <div
                            className="add-domain-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Add New Domain</h3>

                            <input
                                type="text"
                                className="modal-input"
                                placeholder="Enter domain name"
                                value={newDomainName}
                                onChange={(e) => setNewDomainName(e.target.value)}
                            />

                            <input
                                type="number"
                                className="modal-input"
                                placeholder="Enter order ID (e.g. 1)"
                                value={newDomainOrder}
                                onChange={(e) => setNewDomainOrder(e.target.value)}
                            />

                            <button
                                className="modal-submit-btn"
                                onClick={handleCreateDomain}
                                disabled={creatingDomain}
                            >
                                {creatingDomain ? "Adding..." : "Add Domain"}
                            </button>

                            {creatingDomainError && (
                                <p className="error-text">{creatingDomainError}</p>
                            )}
                        </div>
                    </div>
                )}

                {(creatingDomain || deletingDomain) && (
                
                    <div className="loader-overlay">
                        <div className="loader-popup">
                            <div className="spinner" />
                            <p>Please wait...</p>
                        </div>
                    </div>

                )}

            </div>
        </div>
    );
}
